import { useEffect, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { PhoneInput } from '@opendoor/atoms/phone-input';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconCircleCheck } from '@tabler/icons-react';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import {
  AVAILABLE_SOON_NOTIFY_CONSENT_VERSION,
  AVAILABLE_SOON_NOTIFY_SMS_CONSENT,
} from 'components/marketplace/shared/availableSoon';
import { e164UsPhone } from 'components/shared/phone';
import { ClientError } from 'graphql-request';
import Link from 'next/link';

import { BuyerListingNotificationOptInDocument } from './__generated__/available-soon-notify.zeus';

type AvailableSoonNotifyCardProperty = Pick<
  MarketplaceProperty,
  'addressToken' | 'estimatedMonthlyPayment' | 'listPrice'
>;

type SubmitState = 'idle' | 'submitting' | 'success';

const SUCCESS_BODY = 'We’ll text you when this home becomes available.';

export const AvailableSoonNotifyCard = ({
  property,
}: {
  property: AvailableSoonNotifyCardProperty;
}) => {
  const { authentication, user } = useAuth();
  const isLoggedIn = authentication.state === 'authenticated';
  const accountPhone = isLoggedIn ? (user?.primaryPhone ?? null) : null;

  // Null until the viewer types, so late-resolving auth prefills without
  // clobbering input; the typed value wins once present.
  const [typedPhone, setTypedPhone] = useState<string | null>(null);
  const phone = typedPhone ?? accountPhone ?? '';
  const [state, setState] = useState<SubmitState>('idle');
  // invalidNumber drives aria-invalid and the red field treatment, so request
  // failures must not mark a valid number invalid.
  const [error, setError] = useState<{ message: string; invalidNumber: boolean } | null>(null);
  const successRef = useRef<HTMLParagraphElement>(null);

  // The submit button unmounts on success; land focus on the confirmation
  // instead of dropping it to the document body.
  useEffect(() => {
    if (state === 'success') {
      successRef.current?.focus();
    }
  }, [state]);

  const onNotify = async () => {
    const e164Phone = e164UsPhone(phone);
    if (!e164Phone) {
      setError({ message: 'Enter a valid phone number.', invalidNumber: true });
      return;
    }

    setError(null);
    setState('submitting');
    try {
      const result = await requestZeusFromBrowser(BuyerListingNotificationOptInDocument, {
        input: {
          addressId: property.addressToken,
          consentLanguage: AVAILABLE_SOON_NOTIFY_CONSENT_VERSION,
          phoneNumber: e164Phone,
        },
      });
      const outcome = result.buyerListingNotificationOptIn;
      if (outcome.__typename === 'CommandResult' && outcome.success) {
        setState('success');
      } else if (outcome.__typename === 'InvalidInputError') {
        setState('idle');
        setError({ message: 'Enter a valid phone number.', invalidNumber: true });
      } else if (outcome.__typename === 'NotFoundError') {
        // Terminal: no live coming-soon listing, so retrying cannot succeed.
        setState('idle');
        setError({
          message: 'This home is no longer accepting notifications.',
          invalidNumber: false,
        });
      } else {
        setState('idle');
        setError({ message: 'Something went wrong. Try again.', invalidNumber: false });
      }
    } catch (requestError) {
      setState('idle');
      setError({
        message:
          requestError instanceof ClientError && requestError.response.status === 429
            ? 'Too many attempts. Wait a minute and try again.'
            : 'Something went wrong. Try again.',
        invalidNumber: false,
      });
    }
  };

  return (
    <Col className="gap-4 rounded-2xl border border-od-border-primary bg-od-bg-primary px-5 py-6 shadow-[0px_10px_22px_0px_rgba(0,0,0,0.04),0px_2px_12px_0px_rgba(0,0,0,0.04)]">
      <Text as="p" className="text-subheader-xs text-od-content-primary">
        Get notified when this listing becomes available
      </Text>
      <Col className="gap-1">
        <Text as="p" className="text-header-md text-od-content-primary">
          {property.listPrice != null ? formatCurrency(property.listPrice) : 'Price pending'}
        </Text>
        {property.estimatedMonthlyPayment != null && (
          <Text as="p" className="text-body-md text-od-content-secondary">
            {`Est. ${formatCurrency(property.estimatedMonthlyPayment)}/mo`}
          </Text>
        )}
      </Col>
      {/* Always mounted for the live announcement, but out of flow when empty
          so it leaves no visual gap above the form. */}
      <Row
        className={state === 'success' ? 'items-center gap-2' : 'absolute h-0 overflow-hidden'}
        aria-live="polite"
      >
        {state === 'success' && (
          <>
            <IconCircleCheck size={20} className="text-od-content-success" aria-hidden />
            <Text
              as="p"
              ref={successRef}
              tabIndex={-1}
              className="text-body-md text-od-content-primary"
            >
              {SUCCESS_BODY}
            </Text>
          </>
        )}
      </Row>
      {state !== 'success' && (
        <>
          <Row className="items-stretch gap-3">
            <PhoneInput
              value={phone}
              onValueChange={(value) => {
                setTypedPhone(value);
                setError(null);
              }}
              size="xs"
              placeholder="(555) 000-0000"
              className="h-14 w-full"
              aria-label="Phone number"
              aria-describedby={
                error
                  ? 'available-soon-notify-consent available-soon-notify-error'
                  : 'available-soon-notify-consent'
              }
              aria-invalid={error?.invalidNumber === true}
              error={error?.invalidNumber === true}
              analyticsName="cosmos-marketplace-notify-me-phone"
            />
            <Button
              variant="primary"
              size="lg"
              onClick={onNotify}
              loading={state === 'submitting'}
              aria-label="Notify Me"
              analyticsName="cosmos-marketplace-notify-me-cta"
            >
              Notify Me
            </Button>
          </Row>
          {error != null && (
            <Text
              as="p"
              id="available-soon-notify-error"
              role="alert"
              className="text-body-sm text-od-content-error"
            >
              {error.message}
            </Text>
          )}
          {accountPhone && (
            <Text as="p" className="text-body-sm text-od-content-secondary">
              Wrong number?{' '}
              <Link href="/account" className="underline">
                Edit your profile
              </Link>
            </Text>
          )}
          <Text
            as="p"
            id="available-soon-notify-consent"
            className="text-body-sm text-od-content-tertiary"
          >
            {AVAILABLE_SOON_NOTIFY_SMS_CONSENT}
          </Text>
        </>
      )}
    </Col>
  );
};
